/**
 * The owner talking to their Nob INSIDE one knock (ruling 2026-08-25 #9).
 *
 * The same fetch-and-read shape as `streamDoormanTurn`, against a different
 * endpoint and a smaller event set — there are no proposals here and no bio
 * drafts, because this surface has exactly one action and the server decides
 * whether it happened.
 *
 * The refusals carry a SENTENCE where they have one. The per-knock turn cap is
 * a server-only number (FR-9.2 R1) and the server states the consequence
 * instead, so the client renders what it was told rather than keeping a second
 * copy of a rule it cannot see.
 */
import { API_URL } from "./core";
import { parseFrame } from "../sse";

export type OwnerChatEvent =
  | { type: "token"; delta: string }
  /**
   * A line that left the house — the doorman's question, now standing in the
   * hearing both sides read (R-0910-11). `content` is the line as the SENDER
   * reads it; the owner's desk is what wraps it in "Asked them".
   */
  | { type: "relay"; content: string }
  /**
   * THE VERB THE OWNER ASKED FOR (R-0910-13 rule 3). The Nob cannot move the
   * gate and says so; `verb_hint` is what lets the screen put the button that
   * can right under the answer, so the honest reply costs one tap. `null` when
   * the owner asked for no such thing.
   */
  | { type: "done"; message_id: string; verb_hint: GateVerbHint | null }
  | { type: "error"; code: string; message: string | null };

/** The three verbs the owner's own hand presses, as the wire names them. */
export type GateVerbHint = "let_in" | "decline" | "block";

const VERB_HINTS: readonly GateVerbHint[] = ["let_in", "decline", "block"];

export async function* streamOwnerChat(
  knockId: string,
  message: string,
  signal?: AbortSignal,
): AsyncGenerator<OwnerChatEvent> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1/knocks/${encodeURIComponent(knockId)}/owner-chat`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", accept: "text/event-stream" },
      body: JSON.stringify({ message }),
      ...(signal ? { signal } : {}),
    });
  } catch {
    if (!signal?.aborted) yield { type: "error", code: "NETWORK", message: null };
    return;
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code?: unknown; details?: unknown };
    };
    const code =
      res.status === 401
        ? "UNAUTHENTICATED"
        : typeof body.error?.code === "string"
          ? body.error.code
          : "UNKNOWN";
    const details = (
      body.error?.details && typeof body.error.details === "object" ? body.error.details : {}
    ) as Record<string, unknown>;
    yield {
      type: "error",
      code,
      message: typeof details["message"] === "string" ? details["message"] : null,
    };
    return;
  }
  if (!res.body) {
    yield { type: "error", code: "NETWORK", message: null };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      for (;;) {
        const at = buffer.indexOf("\n\n");
        if (at === -1) break;
        const frame = parseFrame(buffer.slice(0, at));
        buffer = buffer.slice(at + 2);
        const event = frame && toOwnerChatEvent(frame);
        if (event) yield event;
      }
    }
  } catch {
    if (!signal?.aborted) yield { type: "error", code: "NETWORK", message: null };
  } finally {
    reader.releaseLock();
  }
}

function toOwnerChatEvent(frame: { event: string; data: string }): OwnerChatEvent | null {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(frame.data) as Record<string, unknown>;
  } catch {
    return null;
  }
  switch (frame.event) {
    case "token":
      return typeof data["delta"] === "string" ? { type: "token", delta: data["delta"] } : null;
    case "relay":
      return typeof data["content"] === "string"
        ? { type: "relay", content: data["content"] }
        : null;
    case "done": {
      const hint = data["verb_hint"];
      return {
        type: "done",
        message_id: typeof data["message_id"] === "string" ? data["message_id"] : "",
        // An allowlist, not a cast: the button this names runs a decision that
        // moves the gate, and it is chosen by the three words the server says
        // or by nothing at all.
        verb_hint: VERB_HINTS.find((verb) => verb === hint) ?? null,
      };
    }
    case "error":
      return {
        type: "error",
        code: typeof data["code"] === "string" ? data["code"] : "UNKNOWN",
        message: null,
      };
    default:
      return null;
  }
}
